import { CopyIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import {
  Center,
  Grid,
  GridItem,
  Icon,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useClipboard,
} from '@chakra-ui/react'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { IoEllipsisHorizontal } from 'react-icons/io5'
import { colors } from '../../../ui-kit'
import type { Webhook } from '../../interfaces/WebhooksGetResponse'
import useToast from '../../hooks/useToast'

const EndpointUrlTd = ({
  endpoint,
}: {
  endpoint: string
}): React.ReactElement => {
  const { onCopy } = useClipboard(endpoint)
  const toast = useToast()
  const { t } = useTranslation()

  const copyUrl = (): void => {
    onCopy()
    toast({
      description: t(
        'webhook_url_copied_to_clipboard',
        'Webhook URL copied to clipboard',
      ),
      status: 'success',
      position: 'top-right',
      isClosable: true,
      duration: 4000,
    })
  }
  return (
    <Td cursor="pointer" onClick={copyUrl}>
      <Grid templateColumns="auto 20px">
        <GridItem>
          <Text wordBreak="break-all">{endpoint}</Text>
        </GridItem>
        <GridItem>
          <Center height="100%">
            <CopyIcon float="right" />
          </Center>
        </GridItem>
      </Grid>
    </Td>
  )
}

const SecretTd = ({ secret }: { secret: string }): React.ReactElement => {
  const { onCopy } = useClipboard(secret)

  const [hidden, setHidden] = React.useState<boolean>(true)

  const toast = useToast()
  const { t } = useTranslation()

  const copySecret = (): void => {
    onCopy()
    toast({
      description: t(
        'webhook_secret_copied_to_clipboard',
        'Webhook secret copied to clipboard',
      ),
      status: 'success',
      position: 'top-right',
      isClosable: true,
      duration: 4000,
    })
  }

  const toggleHidden = (): void => {
    setHidden(!hidden)
  }

  const secretDisplay = hidden
    ? `${secret.substring(0, 9)}${'*'.repeat(secret.length - 3)}`
    : secret

  return (
    <Td>
      <Grid templateColumns="130px 20px 30px">
        <GridItem>
          <Center>
            <Input
              paddingX={2}
              marginX={2}
              width={125}
              contentEditable={false}
              fontFamily="monospace"
              value={secretDisplay}
            />
          </Center>
        </GridItem>
        <GridItem>
          <Center padding={3} cursor="pointer" onClick={copySecret}>
            <CopyIcon />
          </Center>
        </GridItem>
        <GridItem>
          <Center padding={3}>
            {hidden ? (
              <ViewIcon cursor="pointer" onClick={toggleHidden} />
            ) : (
              <ViewOffIcon cursor="pointer" onClick={toggleHidden} />
            )}
          </Center>
        </GridItem>
      </Grid>
    </Td>
  )
}

const CreatedAtTd = ({
  createdAt,
}: {
  createdAt: string
}): React.ReactElement => {
  const createdAtMoment = moment(new Date(createdAt))
  return (
    <Td>
      <Text width={90}>{createdAtMoment.format('DD MMMM YYYY')}</Text>
      <Text width={90}>{createdAtMoment.format('hh:mm A')}</Text>
    </Td>
  )
}

const WebhookRow = ({
  key,
  webhook,
  openDeleteModal,
  openEditModal,
}: {
  key: string
  webhook: Webhook
  openDeleteModal(id: string | null): void
  openEditModal(id: string | null): void
}): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Tr key={key}>
      <EndpointUrlTd endpoint={webhook.endpoint} />
      <SecretTd secret={webhook.secret} />
      <CreatedAtTd createdAt={webhook.createdAt} />
      {webhook.suspended ? (
        <Td color={colors.appRed}>{t('suspended', 'Suspended')}</Td>
      ) : webhook.enabled ? (
        <Td color={colors.appGreenishTeal}>{t('active', 'Active')}</Td>
      ) : (
        <Td color={colors.appYellowOrange}>{t('disabled', 'Disabled')}</Td>
      )}
      <Td>
        <Menu>
          <MenuButton>
            <Icon as={IoEllipsisHorizontal} />
          </MenuButton>
          <MenuList minWidth="0">
            <MenuItem
              onClick={() => {
                openEditModal(webhook.id)
              }}
            >
              {t('edit', 'Edit')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                openDeleteModal(webhook.id)
              }}
            >
              {t('delete', 'Delete')}
            </MenuItem>
          </MenuList>
        </Menu>
      </Td>
    </Tr>
  )
}

interface Props {
  loading: boolean
  webhooks: Webhook[]
  openDeleteModal(id: string): void
  openEditModal(id: string): void
}

const WebhookTable = ({
  loading,
  webhooks,
  openDeleteModal,
  openEditModal,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Table marginTop="16px">
      <Thead bgColor="appPaleGreyTwo">
        <Tr>
          <Th textTransform="capitalize" letterSpacing="none">
            {t('endpoint_url', 'Endpoint URL')}
          </Th>
          <Th textTransform="capitalize" letterSpacing="none">
            {t('webhook_secret', ' Webhook secret')}
          </Th>
          <Th textTransform="capitalize" letterSpacing="none">
            {t('created_on', 'Created on')}
          </Th>
          <Th textTransform="capitalize" letterSpacing="none">
            {t('status', 'Status')}
          </Th>
          <Th textTransform="capitalize" letterSpacing="none">
            {' ' /* Options row */}
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {loading ? (
          <Tr>
            <Td colSpan={5}>
              <Center>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="appPaleGrey"
                  color="appBlue"
                  size="xl"
                />
              </Center>
            </Td>
          </Tr>
        ) : webhooks.length ? (
          webhooks.map(webhook => (
            <WebhookRow
              key={webhook.id}
              webhook={webhook}
              openDeleteModal={openDeleteModal}
              openEditModal={openEditModal}
            />
          ))
        ) : (
          <Tr>
            <Td colSpan={5}>
              <Center>
                <Text>{t('no_webhooks_created', 'No webhooks created')}</Text>
              </Center>
            </Td>
          </Tr>
        )}
      </Tbody>
    </Table>
  )
}

export default WebhookTable
